import { z } from 'zod';
import { grantSchema } from './permissions.js';
import { recursoSchema } from './assinatura.js';

export const emailSchema = z
  .string({ required_error: 'Informe o e-mail' })
  .trim()
  .toLowerCase()
  .email('E-mail inválido');

/** Telefone brasileiro em E.164: +55 seguido de DDD e número. */
export const phoneSchema = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{9,14}$/, 'Telefone deve estar no formato internacional, ex.: +5511999998888');

export const passwordSchema = z
  .string({ required_error: 'Informe a senha' })
  .min(8, 'A senha precisa de pelo menos 8 caracteres')
  .max(200, 'Senha longa demais')
  .regex(/[a-zA-Z]/, 'A senha precisa de ao menos uma letra')
  .regex(/[0-9]/, 'A senha precisa de ao menos um número');

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Informe a senha'),
  /** Slug do tenant quando o login vem de um subdomínio. */
  tenantSlug: z.string().trim().min(1).max(63).optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const signUpChurchSchema = z.object({
  church: z.object({
    name: z.string().trim().min(3, 'Informe o nome da igreja').max(120),
    slug: z
      .string()
      .trim()
      .toLowerCase()
      .min(3, 'O endereço precisa de pelo menos 3 caracteres')
      .max(63)
      .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Use apenas letras minúsculas, números e hífen'),
    denomination: z.string().trim().max(80).optional(),
    timezone: z.string().trim().default('America/Sao_Paulo'),
  }),
  admin: z.object({
    name: z.string().trim().min(3, 'Informe seu nome').max(120),
    email: emailSchema,
    phone: phoneSchema.optional(),
    password: passwordSchema,
  }),
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: 'É preciso aceitar os termos de uso' }),
  }),
});
export type SignUpChurchInput = z.infer<typeof signUpChurchSchema>;

export const refreshSchema = z.object({
  refreshToken: z.string().min(10).optional(),
});

/**
 * Pedir um link de acesso, ou a redefinição da senha (ADR 0016). O mesmo
 * formato serve aos dois: um endereço de e-mail, e nada mais.
 */
export const magicLinkRequestSchema = z.object({
  email: emailSchema,
  tenantSlug: z.string().trim().min(1).max(63).optional(),
});
export type MagicLinkRequestInput = z.infer<typeof magicLinkRequestSchema>;

export const magicLinkConsumeSchema = z.object({
  token: z.string().min(20, 'Link inválido'),
  tenantSlug: z.string().trim().min(1).max(63).optional(),
});
export type MagicLinkConsumeInput = z.infer<typeof magicLinkConsumeSchema>;

/** Criar a senha nova, com o token que chegou no e-mail. */
export const redefinirSenhaSchema = z.object({
  token: z.string().min(20, 'Link inválido'),
  password: passwordSchema,
});
export type RedefinirSenhaInput = z.infer<typeof redefinirSenhaSchema>;

/**
 * A resposta de quem pede um link.
 *
 * É **sempre a mesma**, exista a conta ou não: responder de formas diferentes
 * transformaria o formulário numa lista de quem serve na igreja (ADR 0016).
 */
export const pedidoDeLinkAceitoSchema = z.object({
  aceito: z.literal(true),
  mensagem: z.string(),
});
export type PedidoDeLinkAceito = z.infer<typeof pedidoDeLinkAceitoSchema>;

export const senhaRedefinidaSchema = z.object({
  ok: z.literal(true),
  /** Quantas sessões caíram junto — a tela conta isso para quem trocou. */
  sessoesEncerradas: z.number().int().min(0),
});
export type SenhaRedefinida = z.infer<typeof senhaRedefinidaSchema>;

export const tenantSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  status: z.enum(['TRIAL', 'ACTIVE', 'PAST_DUE', 'READ_ONLY', 'FROZEN', 'CANCELLED']),
  trialEndsAt: z.string().datetime().nullable(),
});
export type TenantSummary = z.infer<typeof tenantSummarySchema>;

export const sessionUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
  isPlatformUser: z.boolean(),
  /**
   * Fonte maior, contraste reforçado e menos opções por tela (seção 9.5).
   * Vem na sessão porque a interface inteira depende dele desde o primeiro
   * pixel: buscá-lo depois faria a tela mudar de tamanho na cara da pessoa.
   */
  modoSimplificado: z.boolean().default(false),
  /** Áreas cujo tour a pessoa já viu, para ele não repetir. */
  toursVistos: z.array(z.string()).default([]),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const authResultSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int().positive(),
  user: sessionUserSchema,
  tenant: tenantSummarySchema.nullable(),
  /** Tenants em que a pessoa serve — uma pessoa pode servir em mais de uma igreja. */
  tenants: z.array(tenantSummarySchema),
});
export type AuthResult = z.infer<typeof authResultSchema>;

export const abilitiesSchema = z.object({
  grants: z.array(grantSchema),
  roles: z.array(z.object({ slug: z.string(), name: z.string(), scopeKind: z.string(), scopeId: z.string().nullable() })),
  /**
   * O que o plano da igreja abre (seção 11.1). Vem junto das permissões porque
   * a interface esconde as duas coisas pelo mesmo motivo: menu que leva a
   * "não pode" é botão morto (seção 9.1).
   */
  recursos: z.record(recursoSchema, z.boolean()).default({}),
});
export type Abilities = z.infer<typeof abilitiesSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Segundo fator por aplicativo autenticador (seção 8.1)
// ─────────────────────────────────────────────────────────────────────────────

/** Seis dígitos do aplicativo, ou um código de recuperação com hífen. */
export const codigoDeSegundoFatorSchema = z
  .string({ required_error: 'Informe o código' })
  .trim()
  .min(6, 'O código tem seis dígitos')
  .max(20, 'Código longo demais');

export const ligarSegundoFatorSchema = z.object({
  codigo: codigoDeSegundoFatorSchema,
});
export type LigarSegundoFatorInput = z.infer<typeof ligarSegundoFatorSchema>;

export const desligarSegundoFatorSchema = z.object({
  senha: z.string().min(1, 'Confirme sua senha'),
  codigo: codigoDeSegundoFatorSchema,
});
export type DesligarSegundoFatorInput = z.infer<typeof desligarSegundoFatorSchema>;

/**
 * O que a pessoa vê ao começar a ligar. O segredo aparece **uma vez**: depois
 * de ligado, ele nunca mais sai do servidor.
 */
export const inicioDoSegundoFatorSchema = z.object({
  otpauthUrl: z.string(),
  segredo: z.string(),
  segredoLegivel: z.string(),
});
export type InicioDoSegundoFator = z.infer<typeof inicioDoSegundoFatorSchema>;

export const situacaoDoSegundoFatorSchema = z.object({
  ligado: z.boolean(),
  ligadoEm: z.string().datetime().nullable(),
  codigosDeRecuperacaoRestantes: z.number().int().min(0),
  /** Perfis com a chave da igreja na mão são cobrados a ligar. */
  recomendado: z.boolean(),
});
export type SituacaoDoSegundoFator = z.infer<typeof situacaoDoSegundoFatorSchema>;

export const codigosDeRecuperacaoSchema = z.object({
  codigos: z.array(z.string()),
});
export type CodigosDeRecuperacao = z.infer<typeof codigosDeRecuperacaoSchema>;

/**
 * A senha conferiu, mas falta o segundo fator. O desafio vale poucos minutos e
 * não serve para nada além de completar este login.
 */
export const desafioDeSegundoFatorSchema = z.object({
  twoFactorRequired: z.literal(true),
  challenge: z.string(),
  expiresIn: z.number().int().positive(),
  /** Para a tela dizer "abra o aplicativo de fulano@..." sem expor a conta. */
  email: z.string(),
});
export type DesafioDeSegundoFator = z.infer<typeof desafioDeSegundoFatorSchema>;

export const responderSegundoFatorSchema = z.object({
  challenge: z.string().min(10, 'Desafio inválido'),
  codigo: codigoDeSegundoFatorSchema,
});
export type ResponderSegundoFatorInput = z.infer<typeof responderSegundoFatorSchema>;

/** O login termina com sessão, ou com o pedido do segundo fator. */
export const respostaDoLoginSchema = z.union([authResultSchema, desafioDeSegundoFatorSchema]);
export type RespostaDoLogin = z.infer<typeof respostaDoLoginSchema>;
